import { UserContextInterface } from "../state/UserContext";
import { LegacyPermissionType } from "../types/dtos/users/PermissionType";
import { AccessPermissionStringKey } from "../types/access/AccessPermissionStringKey";

export const permissionHelper = {
  /**
   * Checks if the current user has a specific permission.
   * The permission can be either a legacy permission type or an access-based permission string key.
   */
  userHasPermission: (
    userContext: UserContextInterface,
    permissionType: LegacyPermissionType | AccessPermissionStringKey
  ): boolean => {
    var user = userContext.user;
    if (!user || !user.permissions) {
      return false;
    }
    return user.permissions.some((permission) => permission === permissionType);
  },
  /**
   * Checks if the current user has either a legacy permission type or an access-based permission string key.
   */
  userHasEitherPermission: (
    userContext: UserContextInterface,
    legacyPermissionType: LegacyPermissionType,
    accessPermission: AccessPermissionStringKey
  ): boolean => {
    var user = userContext.user;
    if (!user || !user.permissions) {
      return false;
    }
    return user.permissions.some(
      (permission) =>
        permission === legacyPermissionType || permission === accessPermission
    );
  },

  /**
   * Whether the Analytics left/touch nav group should appear.
   * Legacy: unchanged — managers always see it; otherwise `hasAnalyticsAccess` from the API.
   * Access roles: managers still always see it; otherwise requires `VIEW-ANALYTICS` so a stale
   * `hasAnalyticsAccess` flag after permission migration does not show Analytics.
   */
  shouldShowAnalyticsNavigation: (
    userContext: UserContextInterface,
  ): boolean => {
    const user = userContext.user;
    if (!user) {
      return false;
    }
    if (user.isManager) {
      return true;
    }
    if (user.client.permissionMode === "LEGACY-PERMISSIONS") {
      return user.hasAnalyticsAccess;
    }
    return (
      user.permissions?.some((permission) => permission === "VIEW-ANALYTICS") ??
      false
    );
  },

  /**
   * URL-only admin tools (Simple Forms, Advanced Task Types).
   * Legacy clients: isAdmin. Access-roles clients: PLATFORM-CONFIGURATION.
   */
  canAccessUrlOnlyAdminTools: (userContext: UserContextInterface): boolean => {
    const user = userContext.user;
    if (!user) {
      return false;
    }
    if (user.client.permissionMode === "LEGACY-PERMISSIONS") {
      return user.isAdmin;
    }
    return permissionHelper.userHasPermission(
      userContext,
      "PLATFORM-CONFIGURATION"
    );
  },
};

export default permissionHelper;
